import React from 'react';

const DEFAULT_HOME_HERO_DESCRIPTION = 'Discover standout products, fresh arrivals, and practical finds curated for everyday life.';

function getGallery(product, resolveMediaUrl) {
  try {
    const raw = [];
    if (product?.img) raw.push(product.img);
    if (product?.images) {
      const imgs = Array.isArray(product.images) ? product.images : JSON.parse(product.images || '[]');
      raw.push(...imgs);
    }
    const seen = new Set();
    return raw
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .map((s) => resolveMediaUrl ? resolveMediaUrl(s) : s)
      .filter((src) => {
        if (!src) return false;
        if (seen.has(src)) return false;
        const lower = src.toLowerCase();
        const isImage = lower.startsWith('data:image/')
          || lower.match(/\.(jpg|jpeg|png|gif|webp|avif|svg)(\?.*)?$/);
        if (!isImage) return false;
        seen.add(src);
        return true;
      })
      .slice(0, 8);
  } catch {
    return [];
  }
}

function useReducedMotion() {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      const update = () => setReduced(!!mq.matches);
      update();
      mq.addEventListener?.('change', update);
      return () => mq.removeEventListener?.('change', update);
    } catch {
      return undefined;
    }
  }, []);
  return reduced;
}

function SectionNav({ items = [] }) {
  const reducedMotion = useReducedMotion();

}

function MediaCarousel({ slides = [], PVImage, onError, altBase = 'Item image', onImageClick }) {
  const reducedMotion = useReducedMotion();
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => setIdx(0), [slides.join('|')]);

  if (!slides.length) {
    return (
      <div className="rounded-2xl sm:rounded-none border border-gray-100 bg-white p-1 shadow-[0_16px_36px_rgba(15,23,42,0.12)]">
        <div className="rounded-2xl sm:rounded-none bg-gray-50 aspect-square" />
      </div>
    );
  }

  const safeIdx = Math.max(0, Math.min(idx, slides.length - 1));
  const slide = slides[safeIdx];

  return (
    <div className="rounded-2xl sm:rounded-none border border-gray-100 bg-white p-1 shadow-[0_16px_36px_rgba(15,23,42,0.12)]">
      <div className="rounded-2xl sm:rounded-none bg-white overflow-hidden">
        <div className="relative">
          <div className="relative aspect-square">
            <PVImage
              src={slide}
              alt={`${altBase} ${safeIdx + 1}`}
              onError={onError}
              className="absolute inset-0 h-full w-full object-cover cursor-pointer"
              imgProps={{
                fetchpriority: 'high',
                onClick: () => {
                  if (typeof onImageClick === 'function') onImageClick(slide);
                }
              }}
            />
          </div>

          {slides.length > 1 && (
            <div className="absolute left-3 bottom-3 flex items-center gap-2">
              {slides.map((src, i) => {
                const active = i === safeIdx;
                return (
                  <button
                    key={src + i}
                    type="button"
                    onClick={() => setIdx(i)}
                    className={`relative overflow-hidden rounded-lg sm:rounded-none border ${active ? 'border-[var(--accent)] ring-2 ring-[var(--accent)]/40 shadow-sm' : 'border-gray-100 hover:border-[var(--accent)]/60'}`}
                    aria-label={`Select image ${i + 1}`}
                    aria-current={active ? 'true' : undefined}
                  >
                    <PVImage
                      src={src}
                      alt=""
                      onError={onError}
                      className="h-10 w-10 object-cover"
                      imgProps={{ variant: 'thumb', loading: 'lazy', decoding: 'async' }}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FeaturedPanel({
  index,
  product,
  label,
  PVImage,
  resolveMediaUrl,
  handleImgError,
  currency,
  goto,
  addToCart,
  buyNow,
}) {
  const gallery = React.useMemo(() => getGallery(product, resolveMediaUrl), [product, resolveMediaUrl]);
  const inStock = (product?.stock ?? 0) > 0;
  const stockLabel = inStock ? (product.stock <= 5 ? 'Low stock' : 'In stock') : 'Sold out';

  return (
    <section id={`feature-${index}`} className="py-10 sm:py-14">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <div className="text-xs font-semibold tracking-[0.24em] uppercase text-gray-400">
            {String(index + 1).padStart(2, '0')}
          </div>
          <div className="text-xs font-semibold tracking-[0.24em] uppercase text-gray-500">{label}</div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:[grid-template-columns:minmax(0,1fr)_minmax(0,360px)] gap-6 lg:gap-8 items-start lg:items-stretch relative">
          <div className="w-full aspect-square">
            <MediaCarousel
              slides={gallery}
              PVImage={PVImage}
              onError={handleImgError}
              altBase={product?.title || 'Featured product'}
              onImageClick={(src) => goto('product', product, false, src)}
            />
          </div>

          <div
            className="rounded-2xl sm:rounded-none border border-gray-200 bg-white p-5 sm:p-6 shadow-sm w-full relative z-[2] flex flex-col"
            style={{ minHeight: 'clamp(360px, 45vw, 690px)' }}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">{product?.title || 'Featured Item'}</h2>
                <div className="mt-1.5 text-xs sm:text-sm text-gray-600">
                  {product?.category ? product.category : 'Handcrafted drop'}
                  {product?.color ? ` · ${product.color}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700">
                  {stockLabel}
                </span>
              </div>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-gray-700 whitespace-pre-line">
              {product?.description || 'Built by hand, finished with care, and tested before it ships.'}
            </p>


            <div className="mt-auto pt-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Price</div>
                <div className="mt-1 text-2xl font-bold text-gray-900" style={{ color: 'var(--accent)' }}>
                  {typeof currency === 'function' ? currency(product?.price || 0) : product?.price}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {(product?.stock ?? 0) > 0 ? `Drop stock: ${product.stock}` : 'Currently unavailable'}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => goto('product', product)}
                  className="rounded-full sm:rounded-none border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-800 hover:border-[var(--accent)] hover:text-gray-900"
                >
                  View details
                </button>
                <button
                  type="button"
                  onClick={() => addToCart(product)}
                  disabled={!inStock}
                  className="rounded-full sm:rounded-none border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-800 hover:border-[var(--accent)] hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add to cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage({
  settings,
  heroBanners = [],
  bannerIdx = 0,
  onPrevHero,
  onNextHero,
  onSelectHero,
  featured = [],
  cartCount = 0,
  goto,
  addToCart,
  buyNow,
  currency,
  PVImage,
  resolveMediaUrl,
  handleImgError,
  liteMode = false,
}) {
  const reducedMotion = useReducedMotion();
  const homepageBanners = React.useMemo(() => {
    try {
      return (settings?.homepageBanners || [])
        .filter(b => b && b.active !== false && String(b.image || '').trim().length > 0)
        .map(b => ({
          id: b.id || `${b.position || 'between_featured'}_${Math.random().toString(16).slice(2, 6)}`,
          image: b.image,
          href: String(b.href || '').trim(),
          position: b.position || 'between_featured'
        }));
    } catch {
      return [];
    }
  }, [settings?.homepageBanners]);
  const renderBanners = React.useCallback((position) => {
    const items = homepageBanners.filter(b => b.position === position);
    if (!items.length) return null;
    return (
      <section className="w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4">
            {items.map((b) => {
              const href = b.href || '';
              const isExternal = /^https?:\/\//i.test(href);
              const content = (
                <img
                  src={resolveMediaUrl ? resolveMediaUrl(b.image) : b.image}
                  alt=""
                  className="w-full aspect-[16/3] object-cover"
                  loading="lazy"
                  decoding="async"
                />
              );
              const frame = (node) => (
                <div className="w-full overflow-hidden rounded-2xl sm:rounded-none border border-gray-100 bg-white shadow-sm">
                  {node}
                </div>
              );
              if (!href) {
                return (
                  <div key={b.id} className="w-full">
                    {frame(content)}
                  </div>
                );
              }
              return (
                <a
                  key={b.id}
                  href={href}
                  className="block w-full"
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                >
                  {frame(content)}
                </a>
              );
            })}
          </div>
        </div>
      </section>
    );
  }, [homepageBanners, resolveMediaUrl]);
  const coverSlides = React.useMemo(() => {
    try {
      const isImageSrc = (src) => {
        const value = String(src || '').toLowerCase();
        if (!value) return false;
        if (value.startsWith('data:image/')) return true;
        return /\.(jpg|jpeg|png|gif|webp|avif|svg|ico)(\?.*)?$/.test(value);
      };
      const fromBanners = (heroBanners || [])
        .map((b) => (b?.image ? resolveMediaUrl(b.image) : ''))
        .filter((src) => isImageSrc(src));
      return fromBanners.slice(0, 6);
    } catch {
      return [];
    }
  }, [heroBanners, resolveMediaUrl]);

  const coverIdx = Math.max(0, Math.min(bannerIdx, Math.max(0, coverSlides.length - 1)));
  const coverImage = coverSlides[coverIdx] || coverSlides[0] || '';
  const [heroDisplaySrc, setHeroDisplaySrc] = React.useState('');
  const [heroIncomingSrc, setHeroIncomingSrc] = React.useState('');
  const [heroIncomingVisible, setHeroIncomingVisible] = React.useState(false);
  const heroSwapTimer = React.useRef(null);

  React.useEffect(() => {
    if (liteMode) {
      if (!coverImage) {
        setHeroDisplaySrc('');
        setHeroIncomingSrc('');
        setHeroIncomingVisible(false);
        return;
      }
      setHeroDisplaySrc(coverImage);
      setHeroIncomingSrc('');
      setHeroIncomingVisible(false);
      return;
    }
    if (!coverImage) {
      setHeroDisplaySrc('');
      setHeroIncomingSrc('');
      setHeroIncomingVisible(false);
      return;
    }
    if (!heroDisplaySrc) {
      setHeroDisplaySrc(coverImage);
      return;
    }
    if (coverImage === heroDisplaySrc) return;
    if (heroSwapTimer.current) clearTimeout(heroSwapTimer.current);
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setHeroIncomingSrc(coverImage);
      setHeroIncomingVisible(false);
      requestAnimationFrame(() => {
        if (cancelled) return;
        setHeroIncomingVisible(true);
        if (heroSwapTimer.current) clearTimeout(heroSwapTimer.current);
        heroSwapTimer.current = setTimeout(() => {
          setHeroDisplaySrc(coverImage);
          setHeroIncomingSrc('');
          setHeroIncomingVisible(false);
        }, 200);
      });
    };
    img.onerror = () => {
      if (cancelled) return;
      setHeroDisplaySrc(coverImage);
      setHeroIncomingSrc('');
      setHeroIncomingVisible(false);
    };
    img.src = coverImage;
    return () => { cancelled = true; };
  }, [coverImage, heroDisplaySrc, liteMode]);

  React.useEffect(() => {
    return () => { if (heroSwapTimer.current) clearTimeout(heroSwapTimer.current); };
  }, []);

  const navItems = React.useMemo(() => {
    const items = [{ id: 'cover', label: 'Home' }];
    if (featured[0]) items.push({ id: 'feature-0', label: featured[0].title || 'Featured' });
    if (featured[1]) items.push({ id: 'feature-1', label: featured[1].title || 'Next' });
    items.push({ id: 'craft', label: 'Craft' });
    items.push({ id: 'closing', label: 'Shop' });
    return items;
  }, [featured]);
  const homeHeroDescription = typeof settings?.homeHeroDescription === 'string'
    ? settings.homeHeroDescription
    : DEFAULT_HOME_HERO_DESCRIPTION;

  return (
    <div className="bg-white/0">
      <SectionNav items={navItems} />

      {renderBanners('top_of_home')}

      <section id="cover" className="relative overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="grid grid-cols-1 lg:[grid-template-columns:0.9fr_1.1fr] gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h1 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900">
                {settings?.siteName || 'ItemSmith'}
              </h1>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-700">
                {homeHeroDescription}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => goto('catalog')}
                  className="rounded-full sm:rounded-none bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:shadow-lg"
                >
                  Shop the drop
                </button>
                <button
                  type="button"
                  onClick={() => goto('cart')}
                  className="rounded-full sm:rounded-none border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-800 hover:border-[var(--accent)] hover:text-gray-900"
                >
                  View cart{cartCount ? ` (${cartCount})` : ''}
                </button>
              </div>

            </div>

            <div className="order-1 lg:order-2">
              <div className="rounded-2xl sm:rounded-none border border-gray-100 bg-white p-1 shadow-[0_16px_36px_rgba(15,23,42,0.12)]">
                <div className="rounded-2xl sm:rounded-none bg-white overflow-hidden">
                  <div className="relative aspect-[5/4] sm:aspect-[6/5] bg-gray-50">
                    {heroDisplaySrc ? (
                      <>
                        <PVImage
                          src={heroDisplaySrc}
                          alt="ItemSmith featured drop"
                          onError={handleImgError}
                          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-200"
                          imgProps={{ fetchpriority: 'high' }}
                        />
                        {heroIncomingSrc && (
                          <PVImage
                            src={heroIncomingSrc}
                            alt=""
                            onError={handleImgError}
                            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-200"
                            style={{ opacity: heroIncomingVisible ? 1 : 0 }}
                            imgProps={{ fetchpriority: 'high' }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-gray-50 via-white to-gray-100" />
                    )}
                    {coverSlides.length > 1 && (
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2">
                        {coverSlides.map((_, i) => {
                          const active = i === coverIdx;
                          return (
                            <button
                              key={`hero-dot-${i}`}
                              type="button"
                              onClick={() => {
                                try { onSelectHero?.(i); } catch {}
                              }}
                              className="keep-rounded p-0 border-0 bg-transparent"
                              aria-label={`Go to hero slide ${i + 1}`}
                              aria-current={active ? 'true' : undefined}
                            >
                              <span
                                className={`block rounded-full border ${active ? 'bg-[var(--accent)] border-[var(--accent)]' : 'bg-white/80 border-gray-300 hover:border-gray-400'}`}
                                style={{ width: 8, height: 8 }}
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {renderBanners('below_hero')}

      {renderBanners('above_featured')}

      {featured.slice(0, 2).map((p, idx) => (
        <React.Fragment key={p?.id || idx}>
          <FeaturedPanel
            index={idx}
            product={p}
            label="Featured drop"
            PVImage={PVImage}
            resolveMediaUrl={resolveMediaUrl}
            handleImgError={handleImgError}
            currency={currency}
            goto={goto}
            addToCart={addToCart}
            buyNow={buyNow}
          />
          {idx === 0 && renderBanners('between_featured')}
        </React.Fragment>
      ))}

      {renderBanners('below_featured')}

      {renderBanners('above_closing')}

      <section id="closing" className="py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl sm:rounded-none border border-gray-200 bg-gradient-to-br from-white via-white to-slate-50 p-8 sm:p-10 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-10">
              <div className="max-w-xl">
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-gray-500">Ready when you are</div>
                <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-gray-900 leading-tight">
                  Own the drop before it sells out.
                </h2>
                <p className="mt-3 text-sm sm:text-base text-gray-700 leading-relaxed">
                  Browse the collection and grab your favorite before the next forge cycle.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => goto('track')}
                  className="rounded-full sm:rounded-none bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:shadow-lg sm:min-w-[170px] text-center"
                >
                  Track an order
                </button>
                <button
                  type="button"
                  onClick={() => goto('help')}
                  className="rounded-full sm:rounded-none border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-800 hover:border-[var(--accent)] hover:text-gray-900 sm:min-w-[170px] text-center"
                >
                  Contact the smithy
                </button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {renderBanners('below_closing')}
    </div>
  );
}
